import { Injectable } from '@angular/core';

/**
 * Custody mode used whenever `isRegulated` has never been explicitly chosen.
 * Single source of truth for every reader — StorageService.getItemSession()
 * and HeaderNewComponent.syncCustodyFromStorage() — so the switcher and the
 * displayed data can't drift out of sync again.
 */
export const DEFAULT_CUSTODY_MODE_IS_REGULATED = true;

@Injectable({
  providedIn: 'root',
})

/**
 * Export Class
 */
export class StorageService {
  private readonly regulatedStorageKey = 'isRegulated';


  /**
 * Store data in localstorage
 * @param {string} key
 * @param {any} value
 */
  public setItem(key: string, value: any) {
   

    localStorage.setItem(key, value);
  }

  /**
   * Get data from localstorage
   * @param {string} key
   * @return {string} value of the key from local storage
   */
  public getItem(key: string) {
    

    return localStorage.getItem(key);
  }

  /**
   * Remove data from localstorage
   * @param {string} key
   * @return {void}
   */
  public removeItem(key: string) {
    if (key === this.regulatedStorageKey) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      return;
    }

    return localStorage.removeItem(key);
  }

  /**
   * clear data from browser storage
   */
  public clearItem(): void {
    localStorage.clear();
    sessionStorage.clear();
  }


  /**
 * Store data in sessionstorage
 * @param {string} key
 * @param {any} value
 */
  public setItemSession(key: string, value: any): void {
    if (key === this.regulatedStorageKey) {
      // Keep the custody mode available across tabs until logout clears auth.
      const serializedValue = value === true || value === 'true' ? 'true' : 'false';
      localStorage.setItem(key, serializedValue);
      sessionStorage.setItem(key, serializedValue);
      return;
    }

    sessionStorage.setItem(key, value);
  }

  /**
   * Get data from sessionstorage
   * @param {string} key
   * @return {any} value of the key from browser storage
   */
  public getItemSession(key: string): any {
    if (key === this.regulatedStorageKey) {
      const localValue = localStorage.getItem(key);
      if (localValue !== null) {
        return localValue === 'true';
      }

      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue !== null) {
        const normalizedValue = sessionValue === 'true';
        localStorage.setItem(key, normalizedValue ? 'true' : 'false');
        return normalizedValue;
      }

      // No explicit choice stored yet: fall back to the shared default.
      return DEFAULT_CUSTODY_MODE_IS_REGULATED;
    }

    return sessionStorage.getItem(key);
  }

  /**
   * Check whether a session value has actually been stored, as opposed to
   * being unset. Lets a caller distinguish "no selection yet" from an
   * explicit value without changing what getItemSession() returns for
   * every other consumer (e.g. the Regulated header, KYC gating).
   * @param {string} key
   * @return {boolean} true when a value exists in storage for key
   */
  public hasSessionValue(key: string): boolean {
    if (key === this.regulatedStorageKey) {
      return localStorage.getItem(key) !== null || sessionStorage.getItem(key) !== null;
    }

    return sessionStorage.getItem(key) !== null;
  }
}
